// src/components/Dashboard.js

import React, { useEffect, useState } from 'react';
import supabase from '../supabase';
import Onboarding from './Onboarding';
import { useNavigate } from 'react-router-dom';
import { FaLink, FaRobot, FaChartBar, FaFolderOpen, FaLock } from 'react-icons/fa';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (sessionData.session?.user) {
          setUser(sessionData.session.user);

          // Fetch onboarding status using 'user_id'
          const { data, error } = await supabase
            .from('profiles')
            .select('is_onboarded')
            .eq('user_id', sessionData.session.user.id)
            .single();

          if (error) {
            console.error('Error fetching profile:', error.message);
            setError(error.message);
          } else {
            setIsOnboarded(data?.is_onboarded);
          }
        } else {
          setError('No user session found.');
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError('An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleOnboardingSubmit = async (formData) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_onboarded: true, ...formData })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error updating profile:', error.message);
        setError(error.message);
      } else {
        setIsOnboarded(true);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-300 text-xl">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-200 mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-gray-400 text-6xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold text-gray-200 mb-2">Authentication Required</h2>
          <p className="text-gray-400 mb-4">Please log in to access your dashboard.</p>
          <button 
            onClick={() => navigate('/login')} 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (isOnboarded === false) {
    return <Onboarding onSubmit={handleOnboardingSubmit} />;
  }

  // Define the feature cards
  const features = [
    {
      id: 1,
      title: 'Channel Link Tree',
      description: 'Organize and manage all your business channels in one place',
      icon: <FaLink className="w-12 h-12 text-blue-600" />,
      status: 'Coming Soon'
    },
    {
      id: 2,
      title: 'Custom Brand AI Chatbot',
      description: 'Get personalized AI assistance tailored to your brand',
      icon: <FaRobot className="w-12 h-12 text-green-600" />,
      status: 'Coming Soon'
    },
    {
      id: 3,
      title: 'Reporting Dashboard',
      description: 'Track your progress and performance metrics',
      icon: <FaChartBar className="w-12 h-12 text-yellow-600" />,
      status: 'Coming Soon'
    },
    {
      id: 4,
      title: 'Brand Resource Hub',
      description: 'Access templates, guides, and resources for your business',
      icon: <FaFolderOpen className="w-12 h-12 text-purple-600" />,
      status: 'Coming Soon'
    },
  ];

  return (
    <div className="flex flex-col min-h-screen ">
     
      <div className="flex-grow container mx-auto px-4 py-8">
        {/* Welcome Back Card */}
        <div className="bg-gray-900 shadow-md mb-8 p-6">
          <h1 className="text-3xl font-bold text-gray-200">Welcome Back!</h1>
          <p className="mt-2 text-gray-400">
            We're glad to see you again. Keep up the great work on your modules.
          </p>
          <div className="mt-6 flex flex-col md:flex-row items-center justify-between">
            <button
              onClick={() => navigate('/modules')}
              className="mt-4 md:mt-0 bg-blue-400 text-white px-6 py-2 rounded-lg shadow hover:bg-blue-500 transition-colors duration-200"
            >
              Go to Modules
            </button>
          </div>
        </div>

        {/* Quick Stats Section */}
        <div className="bg-gray-900 shadow-md mb-8 p-6">
          <h2 className="text-xl font-bold text-gray-200 mb-4">Your Progress</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-800 rounded-lg">
              <div className="text-3xl font-bold text-blue-400">0</div>
              <div className="text-gray-400 text-sm">Modules Completed</div>
            </div>
            <div className="text-center p-4 bg-gray-800 rounded-lg">
              <div className="text-3xl font-bold text-green-400">0</div>
              <div className="text-gray-400 text-sm">Dialog Flows Finished</div>
            </div>
            <div className="text-center p-4 bg-gray-800 rounded-lg">
              <div className="text-3xl font-bold text-purple-400">0%</div>
              <div className="text-gray-400 text-sm">Overall Progress</div>
            </div>
          </div>
        </div>

        {/* Feature Cards Section */}
        <div className="bg-gray-900 p-6">
          <h2 className="text-2xl font-bold text-gray-200 mb-6">Available Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div
                key={feature.id}
                className="relative group bg-gradient-to-br from-black via-gray-900 to-black rounded-lg shadow-md overflow-hidden cursor-not-allowed"
              >
                <div className="p-6 flex flex-col items-center">
                  {feature.icon}
                  <h3 className="mt-4 text-lg font-medium text-gray-200 text-center">{feature.title}</h3>
                  <p className="mt-2 text-sm text-gray-400 text-center">{feature.description}</p>
                  <div className="mt-3 px-3 py-1 bg-yellow-600 text-yellow-100 text-xs rounded-full">
                    {feature.status}
                  </div>
                </div>
                {/* Overlay on Hover */}
                <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="text-center">
                    <FaLock className="text-4xl text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-300 text-sm">Feature coming soon!</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
